/**
 * Market Watch — live watchlist, quote detail, OI and one-click Buy/Sell.
 *
 * The WebSocket feed is mounted here (and only here): leaving this screen
 * disconnects it, which is the existing behaviour. Ticks carry open interest
 * for F&O instruments, and every row opens the order ticket pre-filled.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Minus,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useMarketStore } from '@/stores/market-store';
import type { TickData } from '@/stores/market-store';
import { useMarketWebSocket } from '@/hooks/use-websocket';
import { api, getErrorMessage } from '@/config/api';
import { cn } from '@/lib/cn';
import { formatINR, formatNumber, formatPct, formatQty, formatTime, signClass, toNum } from '@/lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  TableMessage,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { InstrumentSearch } from '@/features/market/components/InstrumentSearch';
import { isDerivativeSegment } from '@/features/market/instruments';
import type { InstrumentResult } from '@/features/market/components/InstrumentSearch';
import { OrderTicket } from '@/features/orders/components/OrderTicket';
import type { OrderTicketPrefill } from '@/features/orders/components/OrderTicket';

const COLUMN_COUNT = 8;
const STALE_AFTER_MS = 120_000;

/** Age of the last tick our backend saw for this row; null when it never ticked. */
function tickAgeMs(tick: TickData, now: number): number | null {
  if (!tick.updated_at) return null;
  const t = new Date(tick.updated_at).getTime();
  return Number.isNaN(t) ? null : now - t;
}

/** Relative buy vs. sell pressure across the full order book. */
function PressureBar({ buyQty, sellQty }: { buyQty: number; sellQty: number }) {
  const total = buyQty + sellQty;
  const buyPct = total > 0 ? (buyQty / total) * 100 : 50;
  return (
    <div>
      <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
        <div className="bg-buy transition-[width] duration-300" style={{ width: `${buyPct}%` }} />
        <div className="bg-sell transition-[width] duration-300" style={{ width: `${100 - buyPct}%` }} />
      </div>
      <div className="mt-1.5 flex justify-between font-tabular text-xs">
        <span className="text-buy">{formatQty(buyQty)} bid</span>
        <span className="text-sell">{formatQty(sellQty)} ask</span>
      </div>
    </div>
  );
}

function ChangeCell({ tick }: { tick: TickData }) {
  const direction = toNum(tick.change);
  const Icon = direction > 0 ? ArrowUp : direction < 0 ? ArrowDown : Minus;
  return (
    <span className={cn('inline-flex items-center justify-end gap-1 font-tabular', signClass(tick.change))}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {formatNumber(tick.change)} ({formatPct(tick.change_percent)})
    </span>
  );
}

function SideButtons({ tick, onTrade }: { tick: TickData; onTrade: (side: 'B' | 'S') => void }) {
  return (
    <div className="flex gap-1">
      <button
        type="button"
        className="rounded-md border border-buy/30 bg-buy/12 px-2 py-1 text-xs font-semibold text-buy transition-colors hover:bg-buy/20"
        onClick={(e) => {
          e.stopPropagation();
          onTrade('B');
        }}
        aria-label={`Buy ${tick.trading_symbol || tick.instrument_token}`}
      >
        B
      </button>
      <button
        type="button"
        className="rounded-md border border-sell/30 bg-sell/12 px-2 py-1 text-xs font-semibold text-sell transition-colors hover:bg-sell/20"
        onClick={(e) => {
          e.stopPropagation();
          onTrade('S');
        }}
        aria-label={`Sell ${tick.trading_symbol || tick.instrument_token}`}
      >
        S
      </button>
    </div>
  );
}

export default function MarketPage() {
  const { ticks, watchlist, isLoading, error, fetchWatchlist, addToWatchlist, removeFromWatchlist } =
    useMarketStore();
  const { isConnected, feedAttached, feedMessage, subscribe, unsubscribe, reconnect } = useMarketWebSocket();

  const [showSearch, setShowSearch] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedTick, setSelectedTick] = useState<string | null>(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketPrefill, setTicketPrefill] = useState<OrderTicketPrefill | null>(null);
  const [lotSizes, setLotSizes] = useState<Record<string, number>>({});
  const [now, setNow] = useState(() => Date.now());
  const [resubscribing, setResubscribing] = useState(false);

  // Re-evaluate row freshness every 15s so a frozen price becomes visible.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, []);

  /** Drop the broker feed on the backend and rebuild it: fixes a token that stopped ticking. */
  const resubscribe = async () => {
    setResubscribing(true);
    try {
      await api.post('/market/feed/reset');
    } catch {
      /* no feed to reset is fine — reconnecting still rebuilds it */
    } finally {
      reconnect();
      setTimeout(() => setResubscribing(false), 1_500);
    }
  };

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  useEffect(() => {
    if (isConnected && watchlist.length > 0) subscribe(watchlist);
  }, [isConnected, watchlist, subscribe]);

  const handleAddInstrument = async (result: InstrumentResult) => {
    setActionError(null);
    try {
      await api.post('/market/watchlist/add', {
        instrument_token: result.instrument_token,
        exchange_segment: result.exchange_segment,
        trading_symbol: result.trading_symbol,
      });
      addToWatchlist(result.instrument_token, result.trading_symbol, result.exchange_segment);
      if (result.lot_size > 1) setLotSizes((m) => ({ ...m, [result.instrument_token]: result.lot_size }));
      subscribe([result.instrument_token]);
      setShowSearch(false);
    } catch (err) {
      setActionError(getErrorMessage(err));
    }
  };

  const handleRemoveInstrument = async (token: string) => {
    setActionError(null);
    try {
      await api.post('/market/watchlist/remove', { instrument_token: token });
      removeFromWatchlist(token);
      unsubscribe([token]);
      if (selectedTick === token) setSelectedTick(null);
    } catch (err) {
      setActionError(getErrorMessage(err));
    }
  };

  const openTicket = (tick: TickData, side: 'B' | 'S') => {
    setTicketPrefill({
      exchange_segment: tick.exchange_segment || 'nse_cm',
      trading_symbol: tick.trading_symbol,
      instrument_token: tick.instrument_token,
      transaction_type: side,
      lot_size: lotSizes[tick.instrument_token],
      ltp: tick.ltp,
    });
    setTicketOpen(true);
  };

  const watchlistData = useMemo(
    () => watchlist.map((token) => ticks[token]).filter(Boolean) as TickData[],
    [watchlist, ticks]
  );

  const selected = selectedTick ? ticks[selectedTick] : null;
  const selectedIsDerivative = selected ? isDerivativeSegment(selected.exchange_segment || '') : false;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Activity}
        title="Market Watch"
        description="Live prices, open interest and depth for your watchlist"
        meta={
          <>
            <Badge tone={isConnected ? 'success' : 'danger'} icon={isConnected ? Wifi : WifiOff}>
              {isConnected ? 'Socket connected' : 'Socket disconnected'}
            </Badge>
            {isConnected && feedAttached === true && <Badge tone="success">Broker feed live</Badge>}
            {isConnected && feedAttached === false && <Badge tone="warning">No broker feed</Badge>}
          </>
        }
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              loading={resubscribing}
              onClick={resubscribe}
              title="Rebuild the broker feed subscription for this watchlist"
            >
              {isConnected ? 'Resubscribe' : 'Reconnect'}
            </Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setShowSearch(true)}>
              Add instrument
            </Button>
          </>
        }
      />

      {actionError && (
        <Alert tone="danger" onDismiss={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {error && (
        <Alert
          tone="danger"
          title="Couldn't load your watchlist"
          action={
            <Button variant="secondary" size="sm" onClick={fetchWatchlist}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!isConnected && (
        <Alert tone="warning" title="Live feed is not connected">
          {feedMessage || 'Prices below are the last values received. The feed needs an active broker session.'}
        </Alert>
      )}

      {isConnected && feedAttached === false && (
        <Alert tone="warning" title="Connected, but no broker feed is attached">
          {feedMessage || 'Connect your broker in Settings, then refresh this page.'}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* Watchlist */}
        <Card className="xl:col-span-2">
          <CardHeader title="Watchlist" description={`${watchlistData.length} instruments · click a row for depth, B/S to trade`} />
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>Instrument</TH>
                  <TH align="right">LTP</TH>
                  <TH align="right">Change</TH>
                  <TH align="right">High / Low</TH>
                  <TH align="right">Volume</TH>
                  <TH align="right">OI</TH>
                  <TH align="center">Trade</TH>
                  <TH align="right" aria-label="Remove" />
                </TR>
              </THead>
              <TBody>
                {isLoading ? (
                  <TableMessage colSpan={COLUMN_COUNT}>
                    <div className="flex flex-col gap-2 p-4">
                      <Skeleton className="h-8" />
                      <Skeleton className="h-8" />
                      <Skeleton className="h-8" />
                    </div>
                  </TableMessage>
                ) : watchlistData.length === 0 ? (
                  <TableMessage colSpan={COLUMN_COUNT}>
                    <EmptyState
                      icon={Search}
                      title="Your watchlist is empty"
                      description="Search for a stock, future or option to start streaming its price."
                      action={
                        <Button variant="secondary" size="sm" icon={Plus} onClick={() => setShowSearch(true)}>
                          Add instrument
                        </Button>
                      }
                    />
                  </TableMessage>
                ) : (
                  watchlistData.map((tick) => {
                    const active = selectedTick === tick.instrument_token;
                    const oi = toNum(tick.open_interest);
                    const age = tickAgeMs(tick, now);
                    const stale = age === null ? toNum(tick.ltp) <= 0 : age > STALE_AFTER_MS;
                    return (
                      <TR
                        key={tick.instrument_token}
                        interactive
                        onClick={() => setSelectedTick(tick.instrument_token)}
                        className={cn(active && 'bg-surface-2')}
                      >
                        <TD>
                          <div className="font-medium text-fg">{tick.trading_symbol || tick.instrument_token}</div>
                          <div className="font-tabular text-xs text-fg-faint uppercase">
                            {tick.exchange_segment?.replace('_', ' ')}
                          </div>
                        </TD>
                        <TD align="right" className="font-tabular font-medium text-fg">
                          <div>{formatINR(tick.ltp)}</div>
                          <div className={cn('text-[11px] font-normal', stale ? 'text-warning' : 'text-fg-faint')}>
                            {age === null ? (toNum(tick.ltp) > 0 ? 'snapshot' : 'no data yet') : stale ? `stale · ${formatTime(tick.updated_at)}` : formatTime(tick.updated_at)}
                          </div>
                        </TD>
                        <TD align="right">
                          <ChangeCell tick={tick} />
                        </TD>
                        <TD align="right" className="font-tabular text-xs text-fg-muted">
                          <span className="text-profit">{formatNumber(tick.high)}</span>
                          {' / '}
                          <span className="text-loss">{formatNumber(tick.low)}</span>
                        </TD>
                        <TD align="right" className="font-tabular text-fg-muted">
                          {formatQty(tick.volume)}
                        </TD>
                        <TD align="right" className="font-tabular text-fg-muted">
                          {oi > 0 ? formatQty(oi) : '—'}
                        </TD>
                        <TD align="center">
                          <SideButtons tick={tick} onTrade={(side) => openTicket(tick, side)} />
                        </TD>
                        <TD align="right">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Remove ${tick.trading_symbol || tick.instrument_token} from watchlist`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveInstrument(tick.instrument_token);
                            }}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </TableWrap>
        </Card>

        {/* Quote detail */}
        <Card className="h-fit">
          <CardHeader
            title="Quote detail"
            description={selected ? selected.trading_symbol : 'Select an instrument'}
            actions={
              selected ? (
                <div className="flex gap-1.5">
                  <Button variant="primary" size="sm" className="bg-buy hover:bg-buy/90" onClick={() => openTicket(selected, 'B')}>
                    Buy
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => openTicket(selected, 'S')}>
                    Sell
                  </Button>
                </div>
              ) : undefined
            }
          />
          {selected ? (
            <CardBody className="flex flex-col gap-5">
              <div>
                <p className="font-tabular text-2xl font-semibold text-fg">{formatINR(selected.ltp)}</p>
                <p className="mt-1 text-sm">
                  <ChangeCell tick={selected} />
                </p>
              </div>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                {[
                  ['Open', formatNumber(selected.open)],
                  ['Close', formatNumber(selected.close)],
                  ['High', formatNumber(selected.high)],
                  ['Low', formatNumber(selected.low)],
                  ['Volume', formatQty(selected.volume)],
                  ['Last traded qty', toNum(selected.last_traded_qty) > 0 ? formatQty(selected.last_traded_qty) : '—'],
                  ['Last trade', selected.last_traded_time || '—'],
                  ['Feed updated', selected.updated_at ? formatTime(selected.updated_at) : '—'],
                  [
                    'Open interest',
                    toNum(selected.open_interest) > 0
                      ? formatQty(selected.open_interest)
                      : selectedIsDerivative
                        ? 'Awaiting tick'
                        : 'n/a (cash)',
                  ],
                  ['Token', selected.instrument_token],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-fg-faint">{label}</dt>
                    <dd className="font-tabular text-fg-muted">{value}</dd>
                  </div>
                ))}
              </dl>

              <div>
                <p className="mb-2 text-xs font-medium tracking-wider text-fg-faint uppercase">Best bid / ask</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-buy/25 bg-buy/8 px-3 py-2">
                    <p className="text-xs text-fg-faint">Bid</p>
                    <p className="font-tabular text-sm font-medium text-buy">
                      {formatNumber(selected.best_bid_price)}
                    </p>
                    <p className="font-tabular text-xs text-fg-subtle">{formatQty(selected.best_bid_qty)} qty</p>
                  </div>
                  <div className="rounded-lg border border-sell/25 bg-sell/8 px-3 py-2">
                    <p className="text-xs text-fg-faint">Ask</p>
                    <p className="font-tabular text-sm font-medium text-sell">
                      {formatNumber(selected.best_ask_price)}
                    </p>
                    <p className="font-tabular text-xs text-fg-subtle">{formatQty(selected.best_ask_qty)} qty</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-medium tracking-wider text-fg-faint uppercase">Order book pressure</p>
                <PressureBar
                  buyQty={toNum(selected.total_buy_qty)}
                  sellQty={toNum(selected.total_sell_qty)}
                />
              </div>
            </CardBody>
          ) : (
            <EmptyState
              icon={Activity}
              title="No instrument selected"
              description="Pick a row from the watchlist to see depth and quote detail."
            />
          )}
        </Card>
      </div>

      {/* Instrument search */}
      <Modal
        open={showSearch}
        onClose={() => setShowSearch(false)}
        title="Add instrument"
        description="Search your broker's scrip master. F&O segments can be filtered by expiry, strike and option type."
        size="md"
      >
        <InstrumentSearch onSelect={handleAddInstrument} actionLabel="Add" selectedTokens={watchlist} />
      </Modal>

      <OrderTicket open={ticketOpen} onClose={() => setTicketOpen(false)} prefill={ticketPrefill} />
    </div>
  );
}
